import type { Venue } from '../types';
import { resolveVenueSource } from '../lib/venue-source';

/**
 * Small "Found via X" badge with optional click-through to the original
 * source (IG post, Google Maps place page, submitted website, etc.).
 *
 * Renders nothing when we have no useful attribution to show — keeping
 * the surrounding layout clean for legacy venues without source data
 * and without a Place-ID rowKey.
 */
export interface VenueSourceBadgeProps {
  venue: Pick<Venue, 'rowKey' | 'discoverySource' | 'discoverySourceUrl' | 'discoverySourceDetail'>;
  /** Visual variant. `inline` is the small chip used on cards/headers. */
  variant?: 'inline' | 'block';
  /** Optional className appended to the wrapper. */
  className?: string;
}

export default function VenueSourceBadge({ venue, variant = 'inline', className = '' }: VenueSourceBadgeProps) {
  const resolved = resolveVenueSource(venue);
  if (!resolved) return null;

  const { label, icon, url, detail } = resolved;
  const inner = (
    <>
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span>{label}</span>
    </>
  );

  // Block variant: a labelled row used inside the admin Identity panel.
  if (variant === 'block') {
    return (
      <div className={`mh-venue-source-block ${className}`.trim()}>
        <div className="small text-muted">Discovered via</div>
        <div className="d-inline-flex align-items-center gap-1">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="d-inline-flex align-items-center gap-1"
              aria-label={`Open original source for this venue (${label})`}
              title={detail ?? `Open original source: ${label}`}
            >
              {inner}
              <span aria-hidden="true">↗</span>
            </a>
          ) : (
            <span className="d-inline-flex align-items-center gap-1">{inner}</span>
          )}
        </div>
        {detail && <div className="small text-muted mt-1">{detail}</div>}
      </div>
    );
  }

  // Inline chip used on cards and detail-page headers.
  const chipClass = `badge bg-light text-dark border d-inline-flex align-items-center gap-1 mh-venue-source-chip ${className}`.trim();
  const titleAttr = detail ? `${label} — ${detail}` : `Discovered via ${label}`;

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className={`${chipClass} text-decoration-none`}
        title={titleAttr}
        aria-label={`Open original source for this venue (${label})`}
      >
        {inner}
        <span aria-hidden="true">↗</span>
      </a>
    );
  }
  return (
    <span className={chipClass} title={titleAttr}>
      {inner}
    </span>
  );
}
